import React, { useEffect, useState, useRef, lazy } from 'react';
import { PlayIcon, CheckCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCountUp } from '../hooks/useCountUp';
function LazyWistiaEmbed({
  videoId,
  title
}: {
  videoId: string;
  title: string;
}) {
  const [load, setLoad] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setLoad(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '200px'
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className="absolute inset-0 w-full h-full bg-black" data-id="element-102">
      {load ? <iframe src={`https://fast.wistia.net/embed/iframe/${videoId}?web_component=true&seo=false`} title={title} allow="autoplay; fullscreen" frameBorder="0" scrolling="no" className="absolute inset-0 w-full h-full" data-id="element-103" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-104">
          <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center" data-id="element-105">
            <PlayIcon className="w-8 h-8 text-white fill-white" data-id="element-106" />
          </div>
        </div>}
    </div>;
}
export function HeroSection() {
  const count = useCountUp(100, 2500, true);
  return <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden" data-id="element-107">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" data-id="element-108" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-purple-600/20 blur-[120px] rounded-full pointer-events-none" data-id="element-109" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10" data-id="element-110">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-111">
          {/* Left Column: Content */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left" data-id="element-112">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-sm font-medium text-purple-300 mb-6 backdrop-blur-sm" data-id="element-113">
              <span className="relative flex h-2 w-2" data-id="element-114">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" data-id="element-115"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500" data-id="element-116"></span>
              </span>
              Sistema de Volumen Extremo para Meta Ads
            </div>

            <h1 className="font-extrabold text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-6 tracking-tight" data-id="element-117">
              Generá{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-purple-600" data-id="element-118">
                +{count} Creativos
              </span>{' '}
              Que Venden En Solo 10 Minutos
            </h1>

            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed" data-id="element-119">
              Generá{' '}
              <span className="text-white font-medium" data-id="element-120">
                imágenes estáticas listas para Meta Ads
              </span>{' '}
              con los ángulos de venta que realmente convierten.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8" data-id="element-121">
              <a href="#pricing" className="bg-white text-black hover:bg-gray-200 font-bold text-lg py-4 px-8 rounded-full shadow-[0_10px_30px_rgba(255,255,255,0.1)] transform hover:-translate-y-1 transition-all duration-300 text-center" data-id="element-122">
                Generar mis 100 ads
              </a>
              <a href="#hero-video" className="glass-card text-white hover:bg-white/10 font-bold text-lg py-4 px-8 rounded-full transition-all duration-300 flex items-center justify-center gap-2" onClick={e => {
              e.preventDefault();
              document.getElementById('hero-video')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }} data-id="element-123">
                <PlayIcon className="w-5 h-5 fill-current" data-id="element-124" />
                Ver cómo funciona
              </a>
            </div>

            <div className="flex flex-wrap justify-center lg:justify-start gap-3 md:gap-6 text-sm text-gray-400 font-medium" data-id="element-125">
              {['Listo para testear', 'En minutos', 'Sin diseñador'].map((tag, i) => <div key={i} className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/5" data-id="element-126">
                    <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-127" />
                    {tag}
                  </div>)}
            </div>
          </motion.div>

          {/* Right Column: Visual */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-128">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_50px_rgba(139,92,246,0.15)] bg-[#050505]" data-id="element-129">
              {/* Browser Mockup Header */}
              <div className="bg-white/5 border-b border-white/5 px-4 py-3 flex items-center gap-2" data-id="element-130">
                <div className="flex gap-1.5" data-id="element-131">
                  <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-132" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-133" />
                  <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-134" />
                </div>
                <div className="ml-4 flex-1 bg-black/20 h-6 rounded-md max-w-[200px]" data-id="element-135" />
              </div>

              {/* Video Container */}
              <div id="hero-video" className="aspect-video bg-black relative" data-id="element-136">
                <LazyWistiaEmbed videoId="02jku4pxhl" title="100ads web Video" data-id="element-137" />
              </div>
            </div>

            {/* Social Proof - below video on desktop */}
            <div className="hidden md:flex items-center gap-3 mt-5" data-id="element-138">
              <div className="flex -space-x-2" data-id="element-139">
                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="" width="36" height="36" loading="lazy" decoding="async" className="w-9 h-9 rounded-full border-2 border-black object-cover" data-id="element-140" />
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="" width="36" height="36" loading="lazy" decoding="async" className="w-9 h-9 rounded-full border-2 border-black object-cover" data-id="element-141" />
                <img src="https://randomuser.me/api/portraits/men/75.jpg" alt="" width="36" height="36" loading="lazy" decoding="async" className="w-9 h-9 rounded-full border-2 border-black object-cover" data-id="element-142" />
                <img src="https://randomuser.me/api/portraits/women/68.jpg" alt="" width="36" height="36" loading="lazy" decoding="async" className="w-9 h-9 rounded-full border-2 border-black object-cover" data-id="element-143" />
                <div className="w-9 h-9 rounded-full border-2 border-black bg-purple-600 flex items-center justify-center text-[10px] font-bold text-white" data-id="element-144">
                  +
                </div>
              </div>
              <div className="text-xs" data-id="element-145">
                <p className="font-bold text-white" data-id="element-146">+400 InfoCommerces</p>
                <p className="text-gray-400" data-id="element-147">ya escalan con 100ads</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mobile Social Proof */}
        <div className="md:hidden flex items-center justify-center gap-3 mt-8" data-id="element-148">
          <div className="flex -space-x-2" data-id="element-149">
            <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="" width="32" height="32" loading="lazy" decoding="async" className="w-8 h-8 rounded-full border-2 border-black object-cover" data-id="element-150" />
            <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="" width="32" height="32" loading="lazy" decoding="async" className="w-8 h-8 rounded-full border-2 border-black object-cover" data-id="element-151" />
            <img src="https://randomuser.me/api/portraits/men/75.jpg" alt="" width="32" height="32" loading="lazy" decoding="async" className="w-8 h-8 rounded-full border-2 border-black object-cover" data-id="element-152" />
            <img src="https://randomuser.me/api/portraits/women/68.jpg" alt="" width="32" height="32" loading="lazy" decoding="async" className="w-8 h-8 rounded-full border-2 border-black object-cover" data-id="element-153" />
            <div className="w-8 h-8 rounded-full border-2 border-black bg-purple-600 flex items-center justify-center text-[10px] font-bold text-white" data-id="element-154">
              +
            </div>
          </div>
          <div className="text-xs" data-id="element-155">
            <p className="font-bold text-white" data-id="element-156">+400 InfoCommerces</p>
            <p className="text-gray-400" data-id="element-157">ya escalan con 100ads</p>
          </div>
        </div>
      </div>
    </section>;
}